<script setup>
import { reactive, ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { usePlans } from '../../Composables/usePlans.js'
import { useHttpErrorsHandler } from '../../Composables/httpErrorsHandler.js'
import Loader from '../../Components/Loader.vue'
import FormTextField from '../../Components/FormTextField.vue'
import FormNumericField from '../../Components/FormNumericField.vue'
import FormSelectField from '../../Components/FormSelectField.vue'
import FormSwitch from '../../Components/FormSwitch.vue'
import FormMultiSelect from '../../Components/FormMultiSelect.vue'
import Editor from '@tinymce/tinymce-vue'

const router = useRouter()
const tinyMceLoading = ref(true)

function pastePostProcess(plugin, args) {
    args.node.innerHTML = args.node.innerHTML.replace(/style="color: #2d65cd;"/g, '')
    args.node.innerHTML = args.node.innerHTML.replace(/style="color: #212529;"/g, '')
    args.node.querySelectorAll('img').forEach(img => {
        img.classList.add('img-fluid')
    })
}
const { httpErrorsHandler } = useHttpErrorsHandler()

const billingIntervalOptions = [
    { value: 'monthly', text: 'Mensuel (1 mois)' },
    { value: 'annual', text: 'Annuel (12 mois)' },
]

const currencyOptions = [
    { value: 'eur', text: 'EUR' },
]

const form = reactive({
    name: null,
    description: null,
    price: null,
    currency: 'eur',
    billing_interval: 'monthly',
    is_active: true,
    is_popular: false,
    sort_order: 0,
    modules: [],
    hiddenCategories: [],
})

const {
    loading,
    creating,
    modules,
    allCategories,
    errors,
    fetchModules,
    fetchAllCategories,
    createPlan,
} = usePlans({
    onCreated: () => router.back(),
    onError: httpErrorsHandler,
})

onMounted(async () => {
    await Promise.all([
        fetchModules({ paidOnly: true }),
        fetchAllCategories(),
    ])
})

const handleModulesUpdate = (selectedModules) => {
    form.modules = selectedModules ? selectedModules.map(m => m.id) : []
}

// Récupérer un module par son ID
const getModuleById = (moduleId) => {
    return modules.value.find(m => (m.data?.id || m.id) === moduleId)
}

// Récupérer le slug d'un module par son ID
const getModuleSlugById = (moduleId) => {
    const module = getModuleById(moduleId)
    return module ? (module.data?.slug || module.slug) : null
}

// Calculer les avertissements de dépendances manquantes
const moduleDependencyWarnings = computed(() => {
    const warnings = []
    const selectedSlugs = form.modules.map(id => getModuleSlugById(id)).filter(Boolean)

    for (const moduleId of form.modules) {
        const module = getModuleById(moduleId)
        if (!module) continue

        const dependencies = module.data?.dependencies || module.dependencies || []
        const dependencyLabels = module.data?.dependency_labels || module.dependency_labels || []

        if (dependencies.length === 0) continue

        const missingIndices = []
        dependencies.forEach((dep, index) => {
            if (!selectedSlugs.includes(dep)) {
                missingIndices.push(index)
            }
        })

        if (missingIndices.length > 0) {
            const moduleLabel = module.data?.name || module.name
            const missingLabels = missingIndices.map(i => dependencyLabels[i] || dependencies[i])

            warnings.push({
                module: moduleLabel,
                missing: missingLabels,
            })
        }
    }

    return warnings
})

const handleHiddenCategoriesUpdate = (selectedCategories) => {
    form.hiddenCategories = selectedCategories || []
}

const handleSubmit = async () => {
    // Protection contre les double-clics
    if (creating.value) return

    // Build hidden_categories array in the format expected by backend
    const hiddenCategories = form.hiddenCategories.map(cat => ({
        category_id: cat.categoryId,
        category_type: cat.categoryType,
    }))

    const formData = {
        name: form.name,
        description: form.description,
        price_cents: Math.round(parseFloat(form.price || 0) * 100),
        currency: form.currency,
        billing_interval: form.billing_interval,
        is_active: form.is_active,
        is_popular: form.is_popular,
        sort_order: form.sort_order,
        modules: form.modules,
        hidden_categories: hiddenCategories,
    }

    await createPlan(formData)
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card" v-show="loading || creating">
                <div class="card-body">
                    <Loader />
                </div>
            </div>
            <form @submit.prevent="handleSubmit" v-show="!loading && !creating">
                <!-- Informations du plan -->
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            <i class="ri-file-list-3-line me-2 align-middle fs-4"></i>Créer un abonnement
                        </h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <FormTextField
                                name="name"
                                label="Nom du plan"
                                :label-hidden="false"
                                :errors="errors"
                                :required="true"
                                :data="form.name"
                                @update:field="form.name = $event"
                                bs-class="col-md-12"
                            />

                            <div class="col-md-12 mb-3">
                                <label for="tinyEditor" class="form-label">Description</label>
                                <div class="text-center my-2" v-if="tinyMceLoading">
                                    <div class="spinner-grow text-dark" role="status"></div>
                                </div>
                                <Editor
                                    id="tinyEditor"
                                    v-model="form.description"
                                    license-key="gpl"
                                    :init="{
                                        base_url: '/tinymce/js/tinymce',
                                        suffix: '.min',
                                        contextmenu: false,
                                        browser_spellcheck: true,
                                        language: 'fr_FR',
                                        menubar: false,
                                        content_style: 'body { font-family: \'Inter\', sans-serif; font-size: 13px; }',
                                        statusbar: false,
                                        plugins: ['lists', 'link', 'autoresize'],
                                        toolbar: 'bold italic underline | bullist numlist | link',
                                        paste_data_images: false,
                                        paste_as_text: false,
                                        paste_webkit_styles: 'color font-weight strong',
                                        autoresize_bottom_margin: 0,
                                        min_height: 150,
                                        max_height: 400,
                                        init_instance_callback: function(editor) {
                                            tinyMceLoading = false;
                                        },
                                        paste_postprocess: pastePostProcess
                                    }"
                                />
                            </div>

                            <FormNumericField
                                name="price"
                                label="Prix"
                                :errors="errors"
                                :required="true"
                                :data="form.price"
                                @update:field="form.price = $event"
                                bs-class="col-md-4"
                                :step="0.01"
                                :min="0"
                                placeholder="29.99"
                            />

                            <FormSelectField
                                name="currency"
                                label="Devise"
                                :label-hidden="false"
                                :errors="errors"
                                :required="true"
                                :data="form.currency"
                                @update:field="form.currency = $event"
                                :liste-options="currencyOptions"
                                bs-class="col-md-4"
                            />

                            <FormSelectField
                                name="billing_interval"
                                label="Intervalle de facturation"
                                :label-hidden="false"
                                :errors="errors"
                                :required="true"
                                :data="form.billing_interval"
                                @update:field="form.billing_interval = $event"
                                :liste-options="billingIntervalOptions"
                                bs-class="col-md-4"
                            />

                            <FormSwitch
                                name="is_active"
                                label="Plan actif"
                                :label-hidden="false"
                                :errors="errors"
                                :data="form.is_active"
                                @update:field="form.is_active = $event"
                                bs-class="col-md-4"
                            />
                        </div>
                    </div>
                </div>

                <!-- Modules inclus -->
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            <i class="ri-apps-line me-2 align-middle fs-4"></i>Modules inclus
                        </h4>
                    </div>
                    <div class="card-body">
                        <div class="row">
                            <FormMultiSelect
                                name="modules"
                                label="Sélectionner les modules"
                                :label-hidden="false"
                                :errors="errors"
                                :liste-options="modules.map(m => ({ id: m.data?.id || m.id, name: m.data?.name || m.name }))"
                                @update:field="handleModulesUpdate"
                                placeholder="Sélectionner les modules..."
                                bs-class="col-md-12"
                            />

                            <div v-if="form.modules.length > 0" class="col-12">
                                <small class="text-muted">
                                    {{ form.modules.length }} module(s) sélectionné(s)
                                </small>
                            </div>

                            <!-- Avertissements de dépendances manquantes -->
                            <div v-if="moduleDependencyWarnings.length > 0" class="col-12 mt-2">
                                <div
                                    v-for="(warning, index) in moduleDependencyWarnings"
                                    :key="index"
                                    class="alert alert-warning alert-border-left shadow mb-2 d-flex align-items-center"
                                    role="alert"
                                >
                                    <i class="ri-alert-line me-3 align-middle fs-16"></i>
                                    <div>
                                        <strong>{{ warning.module }}</strong> nécessite :
                                        <span
                                            v-for="(missing, idx) in warning.missing"
                                            :key="missing"
                                        >
                                            <strong>{{ missing }}</strong><span v-if="idx < warning.missing.length - 1">, </span>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Catégories masquées -->
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            <i class="ri-eye-off-line me-2 align-middle fs-4"></i>Catégories masquées
                        </h4>
                    </div>
                    <div class="card-body">
                        <div class="alert alert-info alert-border-left shadow mb-3 d-flex align-items-center" role="alert">
                            <i class="ri-information-line me-3 align-middle fs-16"></i>
                            Les catégories sélectionnées ci-dessous seront masquées pour les abonnés de ce plan.
                        </div>
                        <div class="row">
                            <FormMultiSelect
                                name="hidden_categories"
                                label="Catégories masquées"
                                :label-hidden="false"
                                :errors="errors"
                                :liste-options="allCategories"
                                @update:field="handleHiddenCategoriesUpdate"
                                placeholder="Sélectionner les catégories à masquer..."
                                bs-class="col-md-12"
                            />

                            <div v-if="form.hiddenCategories.length > 0" class="col-12">
                                <small class="text-muted">
                                    {{ form.hiddenCategories.length }} catégorie(s) masquée(s)
                                </small>
                            </div>

                            <div class="d-flex gap-2 justify-content-end mt-3">
                                <button type="button" class="btn btn-soft-dark" @click="router.back()">Retour</button>
                                <button type="submit" class="btn btn-secondary" :disabled="creating">
                                    <span v-if="creating" class="spinner-border spinner-border-sm me-1"></span>
                                    Valider
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>
